import Toast from "../../../../common/core-ui/Toast.tsx";
import { Link, useFetchers } from "react-router-dom";
import useSingleToast from "../../../root/core-ui/toast/useSingleToast.ts";
import { useIntl } from "react-intl";
import { useCallback, useMemo, useRef } from "react";
import {
  Favorite,
  ManageAsset,
} from "../../../../common/api/decimal/common-types/manageAssets.ts";
import { favoritesByVbidKey, useFavoritesFetcher } from "./favorites.ts";
import { DisplayableToast } from "../../../root/core-ui/toast/types.ts";

// sits on a revalidating favorite data source
// favorite undefined is supported, e.g. for a cached search item
// result that was favorited but then removed, here favorite is presumed false
export default function useFavorites(
  asset: ManageAsset | VST.Bridge.Asset,
  favorite: Favorite | undefined,
  options: { showToast: boolean } = { showToast: true }
) {
  const [showFavoriteToast, closeFavoriteToast] = useSingleToast();
  const intl = useIntl();
  // get hook instances to point to same fetcher
  const fetcher = useFavoritesFetcher(`fave-${asset.vbid}`);
  const shouldShowToastRef = useRef(false);
  const lastValRef = useRef<boolean>();
  const fetchers = useFetchers();

  // if there are multiple instances only want toast on one
  const doToast = useCallback(
    (toast: DisplayableToast) => {
      if (options.showToast) {
        showFavoriteToast(toast);
      }
    },
    [options.showToast, showFavoriteToast]
  );

  const addToFavorites = useCallback(() => {
    shouldShowToastRef.current = true;
    fetcher.submit(
      { vbid: asset.vbid },
      {
        action: `/actions/user/favorites`,
        method: "PUT",
      }
    );
  }, [asset.vbid, fetcher]);

  const removeFromFavorites = useCallback(() => {
    shouldShowToastRef.current = true;
    fetcher.submit(
      { vbid: asset.vbid },
      {
        action: `/actions/user/favorites`,
        method: "DELETE",
      }
    );
  }, [asset.vbid, fetcher]);

  const { formMethod, state, data } = fetcher;

  if (
    shouldShowToastRef.current &&
    formMethod === "PUT" &&
    state === "loading"
  ) {
    shouldShowToastRef.current = false;
    // got response from server so can show add favorite toast
    if (data?.ok) {
      doToast(
        <Toast
          type="success"
          title={intl.formatMessage(
            {
              defaultMessage: '"{title}" added to Favorites.',
            },
            { title: asset.title }
          )}
        >
          <Link
            to="/favorites"
            className="vst-link"
            onClick={closeFavoriteToast}
          >
            {intl.formatMessage({ defaultMessage: "Go to Favorites" })}
          </Link>
        </Toast>
      );
    } else {
      doToast(
        <Toast
          type="error"
          title={intl.formatMessage(
            {
              defaultMessage:
                'Failed to add "{title}" to Favorites. Please try again later.',
            },
            { title: asset.title }
          )}
        />
      );
    }
  } else if (
    shouldShowToastRef.current &&
    formMethod === "DELETE" &&
    state === "loading"
  ) {
    shouldShowToastRef.current = false;
    if (data?.ok) {
      doToast(
        <Toast
          title={intl.formatMessage(
            {
              defaultMessage: '"{title}" removed from Favorites.',
            },
            { title: asset.title }
          )}
        />
      );
    } else {
      doToast(
        <Toast
          type="error"
          title={intl.formatMessage(
            {
              defaultMessage:
                'Failed to remove "{title}" from Favorites. Please try again later.',
            },
            { title: asset.title }
          )}
        />
      );
    }
  }

  // when idle the value is freshly revalidated, during mutation: on submitting
  // presume success, on loading predict success based off response status
  let isFavorited = favorite?.isFavorited;
  if (state !== "idle" && formMethod === "PUT") {
    isFavorited = state === "loading" ? data?.ok === true : true;
    lastValRef.current = isFavorited;
  } else if (state !== "idle" && formMethod === "DELETE") {
    isFavorited = state === "loading" ? data?.ok !== true : false;
    lastValRef.current = isFavorited;
  }

  if (
    fetchers.find((f) => f.key === favoritesByVbidKey && f.state === "loading")
  ) {
    // normally fetcher stays loading during revalidation so there is no gap
    // between optimism and reality, but in search the revalidation triggers
    // independently so we need to fill in the gap
    isFavorited = lastValRef.current;
  }

  const favoritesMenuLink = useMemo(() => {
    if (isFavorited) {
      return {
        label: intl.formatMessage({
          defaultMessage: "Remove from Favorites",
        }),
        onClick: removeFromFavorites,
      };
    } else {
      return {
        label: intl.formatMessage({ defaultMessage: "Add to Favorites" }),
        onClick: addToFavorites,
      };
    }
  }, [addToFavorites, intl, removeFromFavorites, isFavorited]);

  return useMemo(() => {
    return {
      isFavorited,
      addToFavorites,
      removeFromFavorites,
      favoritesMenuLink,
    };
  }, [addToFavorites, isFavorited, favoritesMenuLink, removeFromFavorites]);
}
